import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { DollarSign, TrendingUp, Clock, ArrowUpRight, ArrowDownRight, Search, ChevronDown, ChevronUp, RefreshCw, Download, AlertCircle, Database } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

interface VendorBalance {
  vendor_id: string;
  vendor_name: string;
  vendor_email: string;
  available_amount_cents: number;
  pending_amount_cents: number;
  total_earned_cents: number;
  total_withdrawn_cents: number;
  last_transaction_at: string | null;
}

interface VendorTransaction {
  id: string;
  vendor_id: string;
  type: 'credit' | 'debit' | 'withdrawal' | 'refund' | 'adjustment';
  net_amount_cents: number;
  commission_amount_cents: number | null;
  gross_amount_cents: number;
  source: string;
  order_id: string | null;
  description: string | null;
  created_at: string;
}

export default function AdminVendorFinances() {
  const { toast } = useToast();
  const [vendorBalances, setVendorBalances] = useState<VendorBalance[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<VendorTransaction[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filterStatus, setFilterStatus] = useState<'all' | 'has-balance' | 'no-balance'>('all');

  // Calculate totals
  const totalPlatformEarnings = vendorBalances.reduce((sum, v) => sum + v.total_earned_cents, 0);
  const totalPending = vendorBalances.reduce((sum, v) => sum + v.pending_amount_cents, 0);
  const totalAvailable = vendorBalances.reduce((sum, v) => sum + v.available_amount_cents, 0);
  const totalWithdrawn = vendorBalances.reduce((sum, v) => sum + v.total_withdrawn_cents, 0);

  // Calculate platform commissions (difference between gross and net)
  const totalCommissions = vendorBalances.reduce((sum, v) => {
    // Commission is approximately the difference between total earned and what vendors received
    // For a more accurate calculation, we'd sum all commission_cents from transactions
    return sum + (v.total_earned_cents * 0.15); // Rough estimate assuming 15% avg commission
  }, 0);

  useEffect(() => {
    fetchVendorBalances();
  }, []);

  useEffect(() => {
    if (selectedVendor) {
      fetchVendorTransactions(selectedVendor);
    }
  }, [selectedVendor]);

  const fetchVendorBalances = async () => {
    try {
      setLoading(true);
      
      // Fetch vendor balances with profile data
      const { data: balancesData, error: balancesError } = await supabase
        .from('vendor_balances')
        .select(`
          vendor_id,
          available_amount_cents,
          pending_amount_cents,
          total_earned_cents,
          total_withdrawn_cents
        `)
        .order('total_earned_cents', { ascending: false });

      if (balancesError) throw balancesError;

      // Fetch profiles separately
      const vendorIds = (balancesData || []).map((b: any) => b.vendor_id);
      
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .in('id', vendorIds);

      if (profilesError) throw profilesError;

      // Create profile map
      const profileMap = new Map();
      (profilesData || []).forEach((p: any) => {
        profileMap.set(p.id, p);
      });

      // Fetch last transaction date for each vendor
      const { data: lastTransactions, error: transactionsError } = await supabase
        .from('vendor_transactions')
        .select('vendor_id, created_at')
        .in('vendor_id', vendorIds)
        .order('created_at', { ascending: false });

      if (transactionsError) throw transactionsError;

      // Get last transaction per vendor
      const lastTransactionMap = new Map<string, string>();
      (lastTransactions || []).forEach((t: any) => {
        if (!lastTransactionMap.has(t.vendor_id)) {
          lastTransactionMap.set(t.vendor_id, t.created_at);
        }
      });

      const balances: VendorBalance[] = (balancesData || []).map((item: any) => {
        const profile = profileMap.get(item.vendor_id);
        return {
          vendor_id: item.vendor_id,
          vendor_name: profile?.full_name || 'Unknown',
          vendor_email: profile?.email || '',
          available_amount_cents: item.available_amount_cents,
          pending_amount_cents: item.pending_amount_cents,
          total_earned_cents: item.total_earned_cents,
          total_withdrawn_cents: item.total_withdrawn_cents,
          last_transaction_at: lastTransactionMap.get(item.vendor_id) || null,
        };
      });

      setVendorBalances(balances);
    } catch (error) {
      console.error('Error fetching vendor balances:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVendorTransactions = async (vendorId: string) => {
    try {
      const { data, error } = await supabase
        .from('vendor_transactions')
        .select('*')
        .eq('vendor_id', vendorId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setTransactions((data || []) as VendorTransaction[]);
    } catch (error) {
      console.error('Error fetching vendor transactions:', error);
    }
  };

  const handleSyncBalances = async () => {
    try {
      setSyncing(true);
      
      // Call the sync function
      const { data, error } = await supabase.rpc('sync_all_vendor_balances');
      
      if (error) throw error;
      
      toast({
        title: 'Synchronisation réussie',
        description: `${data || 0} balance(s) de vendeur synchronisée(s)`,
      });
      
      // Refresh the data
      await fetchVendorBalances();
    } catch (error: any) {
      console.error('Error syncing balances:', error);
      toast({
        title: 'Erreur de synchronisation',
        description: error.message || 'Impossible de synchroniser les balances',
        variant: 'destructive',
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleExportCSV = () => {
    if (filteredBalances.length === 0) {
      toast({
        title: 'Aucune donnée',
        description: 'Aucun vendeur à exporter',
        variant: 'destructive',
      });
      return;
    }

    const csvHeader = 'Vendeur,Email,Disponible,En Attente,Total Gagné,Total Retiré,Dernière Transaction\n';
    const csvRows = filteredBalances.map(v => 
      `"${v.vendor_name}","${v.vendor_email}",${v.available_amount_cents / 100},${v.pending_amount_cents / 100},${v.total_earned_cents / 100},${v.total_withdrawn_cents / 100},"${v.last_transaction_at || 'N/A'}"`
    ).join('\n');
    
    const csv = csvHeader + csvRows;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `balances-vendeurs-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: 'Export réussi',
      description: `${filteredBalances.length} vendeur(s) exporté(s)`,
    });
  };

  const formatAmount = (cents: number) => {
    return `${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(cents / 100)} FC`;
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'credit':
        return <ArrowUpRight className="h-4 w-4 text-green-500" />;
      case 'debit':
      case 'withdrawal':
        return <ArrowDownRight className="h-4 w-4 text-red-500" />;
      default:
        return <RefreshCw className="h-4 w-4 text-blue-500" />;
    }
  };

  const getTransactionBadge = (type: string) => {
    const badges: Record<string, { variant: 'default' | 'destructive' | 'secondary' | 'outline'; label: string }> = {
      credit: { variant: 'default', label: 'Crédit' },
      debit: { variant: 'destructive', label: 'Débit' },
      withdrawal: { variant: 'secondary', label: 'Retrait' },
      refund: { variant: 'outline', label: 'Remboursement' },
      adjustment: { variant: 'outline', label: 'Ajustement' },
    };
    const badge = badges[type] || badges.adjustment;
    return <Badge variant={badge.variant}>{badge.label}</Badge>;
  };

  const filteredBalances = vendorBalances.filter((v) => {
    const matchesSearch = v.vendor_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.vendor_email.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filterStatus === 'has-balance') {
      return matchesSearch && (v.available_amount_cents > 0 || v.pending_amount_cents > 0);
    } else if (filterStatus === 'no-balance') {
      return matchesSearch && v.available_amount_cents === 0 && v.pending_amount_cents === 0;
    }
    
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Vendeurs Gagnés</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatAmount(totalPlatformEarnings)}</div>
            <p className="text-xs text-muted-foreground">
              Tous les gains des vendeurs
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Commissions Estimées</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{formatAmount(totalCommissions)}</div>
            <p className="text-xs text-muted-foreground">
              Revenus de la plateforme
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Montants Disponibles</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatAmount(totalAvailable)}</div>
            <p className="text-xs text-muted-foreground">
              Prêt à être retiré
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">En Attente</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{formatAmount(totalPending)}</div>
            <p className="text-xs text-muted-foreground">
              Fonds en cours de traitement
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Vendor Balances Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Balances des Vendeurs</CardTitle>
              <CardDescription>Vue détaillée des finances de chaque vendeur</CardDescription>
            </div>
            <div className="flex items-center space-x-2">
              <Select value={filterStatus} onValueChange={(value: any) => setFilterStatus(value)}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="has-balance">Avec solde</SelectItem>
                  <SelectItem value="no-balance">Sans solde</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher un vendeur..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 w-64"
                />
              </div>
              <Button 
                variant="outline" 
                size="icon" 
                onClick={handleSyncBalances}
                disabled={syncing}
                title="Synchroniser les balances"
              >
                <Database className={`h-4 w-4 ${syncing ? 'animate-pulse' : ''}`} />
              </Button>
              <Button 
                variant="outline" 
                size="icon" 
                onClick={handleExportCSV}
                title="Exporter en CSV"
              >
                <Download className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" onClick={fetchVendorBalances}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Vendeur</TableHead>
                <TableHead>Disponible</TableHead>
                <TableHead>En Attente</TableHead>
                <TableHead>Total Gagné</TableHead>
                <TableHead>Total Retiré</TableHead>
                <TableHead>Dernière Transaction</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredBalances.length === 0 && vendorBalances.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <div className="flex flex-col items-center space-y-3">
                      <AlertCircle className="h-12 w-12 text-muted-foreground" />
                      <div className="text-muted-foreground">
                        <p className="font-medium">Aucune balance de vendeur trouvée</p>
                        <p className="text-sm">Les balances apparaîtront automatiquement lorsque des vendeurs auront des transactions</p>
                      </div>
                      <Button onClick={handleSyncBalances} disabled={syncing} variant="outline">
                        <Database className="h-4 w-4 mr-2" />
                        Synchroniser les balances
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredBalances.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Aucun vendeur trouvé avec ces critères
                  </TableCell>
                </TableRow>
              ) : (
                filteredBalances.map((vendor) => (
                  <TableRow key={vendor.vendor_id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{vendor.vendor_name}</div>
                        <div className="text-sm text-muted-foreground">{vendor.vendor_email}</div>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium text-green-600">
                      {formatAmount(vendor.available_amount_cents)}
                    </TableCell>
                    <TableCell className="text-orange-600">
                      {formatAmount(vendor.pending_amount_cents)}
                    </TableCell>
                    <TableCell>{formatAmount(vendor.total_earned_cents)}</TableCell>
                    <TableCell>{formatAmount(vendor.total_withdrawn_cents)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {vendor.last_transaction_at
                        ? formatDistanceToNow(new Date(vendor.last_transaction_at), {
                            addSuffix: true,
                            locale: fr,
                          })
                        : 'Aucune'}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setSelectedVendor(selectedVendor === vendor.vendor_id ? null : vendor.vendor_id)
                        }
                      >
                        {selectedVendor === vendor.vendor_id ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Transaction Details */}
      {selectedVendor && (
        <Card>
          <CardHeader>
            <CardTitle>Historique des Transactions</CardTitle>
            <CardDescription>
              Transactions pour {vendorBalances.find((v) => v.vendor_id === selectedVendor)?.vendor_name}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Montant Brut</TableHead>
                  <TableHead>Commission</TableHead>
                  <TableHead>Montant Net</TableHead>
                  <TableHead>Description</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      Aucune transaction trouvée
                    </TableCell>
                  </TableRow>
                ) : (
                  transactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell className="text-sm">
                        {new Date(transaction.created_at).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          {getTransactionIcon(transaction.type)}
                          {getTransactionBadge(transaction.type)}
                        </div>
                      </TableCell>
                      <TableCell>
                        {transaction.source === 'order' && transaction.order_id ? (
                          <span className="text-sm font-mono">#{transaction.order_id.slice(0, 8)}</span>
                        ) : (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {transaction.gross_amount_cents
                          ? formatAmount(transaction.gross_amount_cents)
                          : '-'}
                      </TableCell>
                      <TableCell className="text-orange-600">
                        {transaction.commission_amount_cents
                          ? `- ${formatAmount(transaction.commission_amount_cents)}`
                          : '-'}
                      </TableCell>
                      <TableCell className="font-medium">
                        {formatAmount(transaction.net_amount_cents)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {transaction.description || '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
